/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import { Button, Spin, Typography } from 'antd';
import mapboxgl from 'mapbox-gl';

import { CloseOutlined, EnvironmentOutlined } from '@ant-design/icons';

import MapControls from './MapControls';
import {
  clearMapData,
  drawTrackingData,
  fetchFormattedEta,
  fitMapToData,
  initializeMap,
  stopRouteAnimation,
  updateSelectedLocationMarker,
} from './mapUtils';

import 'mapbox-gl/dist/mapbox-gl.css';
import './CustomPopup.scss';

const { Text } = Typography;

const configureMapboxToken = (settings) => {
  if (settings?.mapBoxToken && !mapboxgl.accessToken) {
    mapboxgl.accessToken = settings.mapBoxToken;
  }
};

// Componente que renderiza o mapa de rastreamento
function MapComponent({
  task,
  selectedOperator,
  trackingData,
  loading,
  onFullScreenToggle,
  selectedLocation,
  onLocationSelect,
  settings,
}) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markers = useRef([]);
  const selectedLocationMarker = useRef(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [pointsToBound, setPointsToBound] = useState([]);
  const [eta, setEta] = useState(null);
  const wasLoading = useRef(false);
  const [isFollowingOperator, setIsFollowingOperator] = useState(false);

  const clear = () => clearMapData(map.current, markers);

  useEffect(() => {
    if (map.current) return;

    configureMapboxToken(settings);

    const newMap = initializeMap(mapContainer, setIsMapLoaded);
    if (newMap) {
      map.current = newMap;
      newMap.on('dragstart', () => setIsFollowingOperator(false));
    }

    // eslint-disable-next-line consistent-return
    return () => {
      stopRouteAnimation(map.current);
      map.current?.remove();
      map.current = null;
    };
  }, [settings]);

  // Efeito para desenhar a rota e os marcadores sempre que os dados mudam
  useEffect(() => {
    if (isMapLoaded) {
      clear();
      drawTrackingData(map.current, trackingData, markers, task, selectedOperator).then(
        (newPoints) => {
          setPointsToBound(newPoints);
        }
      );
    }
  }, [trackingData, isMapLoaded, task, selectedOperator]);

  // Efeito para ajustar o zoom apenas quando novos dados são carregados (e não para cada ponto)
  useEffect(() => {
    // Se estava carregando e agora não está mais (dados carregados)
    if (wasLoading.current && !loading && isMapLoaded && trackingData.length > 0) {
      fitMapToData(map.current, trackingData, task);
    }
    wasLoading.current = loading;
  }, [loading, isMapLoaded, trackingData, task]);

  // Efeito para seguir o operador se o modo estiver ativo
  useEffect(() => {
    if (isFollowingOperator && trackingData?.length) {
      const sorted = [...trackingData].sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
      );
      const lastPoint = sorted[sorted.length - 1];
      map.current?.flyTo({ center: [lastPoint.longitude, lastPoint.latitude], zoom: 17 });
    }
  }, [isFollowingOperator, trackingData]);

  // Gerencia o marcador de localização selecionada
  useEffect(() => {
    updateSelectedLocationMarker(map.current, selectedLocation, selectedLocationMarker);
  }, [selectedLocation]);

  // Calcula ETA
  useEffect(() => {
    let mounted = true;
    (async () => {
      const value = await fetchFormattedEta(selectedOperator, trackingData, task);
      if (mounted) setEta(value);
    })();
    return () => {
      mounted = false;
    };
  }, [trackingData, selectedOperator, task]);

  const handleCenterOnStart = () => {
    if (!map.current || !trackingData?.length) return;
    const sorted = [...trackingData].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    map.current.flyTo({ center: [sorted[0].longitude, sorted[0].latitude], zoom: 17 });
  };

  const handleCenterOnDestination = () => {
    if (!map.current) return;
    const destLat = parseFloat(task?.latitude);
    const destLng = parseFloat(task?.longitude);
    if (Number.isNaN(destLat) || Number.isNaN(destLng)) return;
    map.current.flyTo({ center: [destLng, destLat], zoom: 17 });
  };

  const handleCenterOnOperator = () => {
    const nextFollowingState = !isFollowingOperator;
    setIsFollowingOperator(nextFollowingState);

    if (nextFollowingState && map.current && trackingData?.length) {
      if (selectedOperator?.trackingEnd) {
        setIsFollowingOperator(false);
        return;
      }
      const sorted = [...trackingData].sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
      );
      const operatorPoint = sorted[sorted.length - 1]; // Última posição do operador
      map.current.flyTo({ center: [operatorPoint.longitude, operatorPoint.latitude], zoom: 17 });
    }
  };

  return (
    <div className="map-component">
      <MapControls
        onCenter={handleCenterOnStart}
        onCenterDestination={handleCenterOnDestination}
        onCenterOperator={handleCenterOnOperator}
        onFit={() => fitMapToData(map.current, trackingData, task)}
        onToggleFullScreen={onFullScreenToggle}
        fitDisabled={pointsToBound.length === 0}
        routeDisabled={!trackingData?.length}
        selectedOperator={selectedOperator}
        eta={eta}
        isFollowingOperator={isFollowingOperator}
      />
      <div className={`map-container-wrapper ${!trackingData?.length ? 'disabled' : ''}`}>
        {selectedLocation && (
          <div className="selected-location-indicator">
            <Text>
              Lat: {selectedLocation.lat.toFixed(6)}, Lng: {selectedLocation.lng.toFixed(6)}
            </Text>
            <Button
              size="small"
              type="text"
              icon={<CloseOutlined />}
              onClick={() => onLocationSelect(null)}
              disabled={false}
            />
          </div>
        )}
        {selectedOperator?.trackingStart && !selectedOperator?.trackingEnd && (
          <div className="route-status-badge">
            <EnvironmentOutlined /> EM PERCURSO
          </div>
        )}
        <div ref={mapContainer} className="map-container" />
        {(loading || !isMapLoaded) && (
          <div className="map-loading">
            <Spin />
            <span>{loading ? 'Carregando dados...' : 'Inicializando mapa...'}</span>
          </div>
        )}
        {!loading && !trackingData?.length && (
          <div className="map-no-data">
            <span>Nenhum dado de rastreamento para exibir</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default MapComponent;
